import { useEffect, useState } from "react";
import type { MenuFeedResult } from "@shared/types/foyer";
import { getBinding } from "../../lib/binding";

/** How often a TV re-asks for its menu. The server holds its own (much longer)
 *  upstream TTL, so this is only about picking up a kitchen's mid-day
 *  correction without needing a reboot - it costs one cheap cached response. */
const POLL_MS = 15 * 60 * 1000;

export interface MenuFeedState {
  result: MenuFeedResult | null;
  /** True until the first answer lands, so the screen can hold its breath
   *  rather than flashing "no menu" and then filling in. */
  loading: boolean;
  /** True when the fetch failed outright and we have nothing to show. */
  failed: boolean;
}

/**
 * Fetch this premise's parsed RSS menu.
 *
 * The TV sends no URL - it names its own premise and the server resolves the
 * stored feed (see src/server/api/menuFeed.ts). `previewPremiseId` covers the
 * CMS live preview, which has a session but no device binding.
 */
export function useMenuFeed(
  enabled: boolean,
  previewPremiseId?: string
): MenuFeedState {
  const [result, setResult] = useState<MenuFeedResult | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const binding = getBinding();
    const premiseId = binding?.premiseId ?? previewPremiseId;
    if (!premiseId) {
      setLoading(false);
      return;
    }

    let alive = true;
    const headers: Record<string, string> = {};
    if (binding?.screenToken) headers["X-Screen-Token"] = binding.screenToken;

    const load = async () => {
      try {
        const res = await fetch(
          `/api/frontlayer/v1/premise/${encodeURIComponent(premiseId)}/menu-feed`,
          { headers }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as MenuFeedResult;
        if (!alive) return;
        setResult(json);
        setFailed(false);
      } catch {
        if (!alive) return;
        // Keep whatever we already had on screen - a menu from 15 minutes ago
        // is still today's menu. Only report failure when we have nothing.
        setFailed((prev) => (result ? prev : true));
      } finally {
        if (alive) setLoading(false);
      }
    };

    void load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
    // `result` is deliberately NOT a dependency - including it would tear down
    // and rebuild the poll on every successful refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, previewPremiseId]);

  return { result, loading, failed };
}
